<template>
  <div>
    <v-expansion-panels v-model="expanded">
      <v-expansion-panel>
        <v-expansion-panel-header color="primary">
          <v-row>
            <v-icon class="ml-6 mr-4">scatter_plot</v-icon>
            <span class="font-weight-bold mt-1 text-truncate white--text"
              >Scenarios Objectives
            </span>
            <exo-help-dialog
              :color="'white--text'"
              :margin="'ma-0'"
              title="Scenarios Objectives"
            >
              <tooltipParetoFrontViews />
            </exo-help-dialog>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row class="mb-n8">
            <v-col cols="2" />
            <v-col cols="1" class="d-flex justify-center align-center"
              >Display</v-col
            >
            <v-col cols="3">
              <v-select v-model="xAxisField" :items="selectorValues"></v-select>
            </v-col>
            <v-col cols="1" class="d-flex justify-center align-center">
              and
            </v-col>
            <v-col cols="3">
              <v-select v-model="yAxisField" :items="selectorValues"></v-select>
            </v-col>
            <v-col cols="2" />
          </v-row>
          <div
            v-if="isGraphValuesValid"
            style="width: 100%; justify-content: center"
          >
            <div class="ml-0" style="width: 100%; justify-content: center">
              <FullscreenGraphComponent
                :traces="paretoTraces"
                :layout="plotLayout"
                title=""
                style="width: 100%; height: 300px"
              ></FullscreenGraphComponent>
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import dataTools from '../../tools/data.js'
import tooltipParetoFrontViews from '../../components/Tooltips/Defense/tooltipParetoFrontViews'
import FullscreenGraphComponent from './FullscreenGraphComponent'
import asterxMixin from './asterx.mixin.js'

export default {
  name: 'EvasiveManeuversParetoFront',
  components: {
    tooltipParetoFrontViews,
    FullscreenGraphComponent
  },
  props: {
    maneuvers: {
      type: Array,
      default: () => []
    },
    comparedManeuvers: {
      type: Array,
      default: () => []
    },
    fieldsToCompare: {
      type: Array,
      default: () => []
    },
    context: {
      type: String,
      default: 'ReactionManeuverReport'
    }
  },
  mixins: [asterxMixin],
  data() {
    return {
      expanded: 0,
      xAxisField: '',
      yAxisField: '',

      plotConfig: {
        responsive: true,
        modeBarButtonsToRemove: ['select2d', 'lasso2d']
      },
      locallySavedParams: ['xAxisField', 'yAxisField']
    }
  },

  mounted() {
    this.restoreLocallySavedParameters(this.locallySavedParams)
  },
  watch: {
    xValues: {
      handler() {
        this.updateLocallySavedParameters(this.locallySavedParams)
      },
      deep: true
    },
    yValues: {
      handler() {
        this.updateLocallySavedParameters(this.locallySavedParams)
      },
      deep: true
    }
  },
  computed: {
    paretoTraces() {
      return [
        {
          x: this.xValues.map((value) =>
            this.getDisplayValueFromPath(value, [this.xAxisField], this.context)
          ),
          y: this.yValues.map((value) =>
            this.getDisplayValueFromPath(value, [this.yAxisField], this.context)
          ),
          text: this.textValues,
          hovertemplate:
            '%{text} ' +
            '<br><br><b>%{xaxis.title.text}</b>: %{x}' +
            '<br><b>%{yaxis.title.text}</b>: %{y}',
          marker: {
            color: this.colorValues,
            symbol: 'diamond',
            size: 10
          },
          type: 'scatter',
          mode: 'markers',
          showlegend: false,
          name: ''
        }
      ]
    },
    selectorValues() {
      return this.fieldsToCompare.map((field) => {
        return {
          value: field,
          text: this.getLabelFromPath([field], this.context)
        }
      })
    },
    isGraphValuesValid() {
      return this.xAxisField && this.yAxisField
    },
    xValues() {
      if (this.xAxisField) {
        return this.maneuvers.map((maneuver) => maneuver[this.xAxisField])
      } else {
        return []
      }
    },
    yValues() {
      if (this.yAxisField) {
        return this.maneuvers.map((maneuver) => maneuver[this.yAxisField])
      } else {
        return []
      }
    },
    textValues() {
      return this.maneuvers.map((maneuver) => {
        const selectedLabel = this.isManeuverSelected(maneuver)
          ? ' (selected)'
          : ''
        return `Maneuver ${maneuver.id}${selectedLabel}`
      })
    },
    colorValues() {
      const unselectedColor = this.$vuetify.theme.dark ? 'white' : '#1E1E1E'
      return this.maneuvers.map((maneuver) =>
        this.isManeuverSelected(maneuver) ? '#5757ff' : unselectedColor
      )
    },
    comparedManeuversIds() {
      return this.comparedManeuvers.map((maneuver) => maneuver.id).sort()
    },
    plotLayout() {
      return {
        plot_bgcolor: this.$vuetify.theme.dark ? '#1E1E1E' : 'white',
        paper_bgcolor: this.$vuetify.theme.dark ? '#1E1E1E' : 'white',
        font: { color: this.$vuetify.theme.dark ? 'white' : '#121212' },
        xaxis: {
          title: {
            text:
              this.getLabelFromPath([this.xAxisField], this.context) +
              this.getUnitLabelFromPath([this.xAxisField], this.context)
          }
        },
        yaxis: {
          title: {
            text:
              this.getLabelFromPath([this.yAxisField], this.context) +
              this.getUnitLabelFromPath([this.yAxisField], this.context)
          }
        },
        margin: { t: 0 }
      }
    }
  },
  methods: {
    isManeuverSelected(maneuver) {
      return this.comparedManeuversIds.includes(maneuver.id)
    },
    getUnitLabelFromPath(fieldPath, context) {
      const fieldType = dataTools.getFieldTypeFromPath(fieldPath, context, null)
      return dataTools.getUnitLabel(dataTools.getDefaultGuiUnit(fieldType))
    },
    getLabelFromPath(fieldPath, context) {
      return dataTools.getFieldLabelFromPath(fieldPath, context)
    },
    getDisplayValueFromPath(value, fieldPath, context) {
      const fieldType = dataTools.getFieldTypeFromPath(fieldPath, context, null)
      return dataTools.convertSi2gui(fieldType, value)
    }
  }
}
</script>
